import React, { useState, useEffect, useCallback, useRef } from 'react';
import Draggable from 'react-draggable';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  X, Plus, Copy, ClipboardPaste, Save, Filter, Trash2, 
  GripHorizontal, Minimize2, Maximize2, RefreshCw, MapPin,
  ChevronDown, ChevronUp, Edit, Bus, Clock, MapPinned, Phone, FileText, Euro, Users
} from 'lucide-react';
import { TimeInput24h } from '@/components/ui/time-input-24h';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Format date to YYYY-MM-DD in local timezone (not UTC)
const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Hook for detecting mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Editable textarea component with local state to preserve cursor position
function EditableTextarea({ value, onChange, onBlur, onFinish, onTab }) {
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef(null);
  
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);
  
  const handleBlur = () => {
    onChange(localValue);
    onBlur();
  };
  
  return (
    <textarea
      ref={textareaRef}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onChange(localValue);
          onFinish();
        }
        if (e.key === 'Enter' && e.ctrlKey) {
          onChange(localValue);
          onFinish();
        }
        if (e.key === 'Tab' && onTab) {
          e.preventDefault();
          onChange(localValue);
          onTab(e.shiftKey);
        }
      }}
      className="w-full border-0 bg-blue-50 text-sm px-1 py-1 resize-none"
      style={{ minHeight: '60px' }}
      placeholder="Ctrl+Enter для завершения"
    />
  );
}

// Editable input component with local state to preserve cursor position
function EditableInput({ value, onChange, onFinish, onTab, type = 'text', className = '' }) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleBlur = () => {
    onChange(localValue);
    onFinish();
  };
  
  return (
    <input
      ref={inputRef}
      type={type}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape' || e.key === 'Enter') {
          onChange(localValue);
          onFinish();
        }
        if (e.key === 'Tab' && onTab) {
          e.preventDefault();
          onChange(localValue);
          onTab(e.shiftKey);
        }
      }}
      className={`w-full border-0 bg-blue-50 text-sm px-1 py-1 ${className}`}
    />
  );
}

// Column definitions
// Initial column definitions with default widths
const DEFAULT_COLUMNS = [
  { key: 'date', label: 'Дата', width: 110, minWidth: 90, type: 'date' },
  { key: 'time_start', label: 'Время', width: 85, minWidth: 70, type: 'time' },
  { key: 'route_name', label: 'Маршрут', width: 150, minWidth: 100, type: 'text' },
  { key: 'description', label: 'Описание', width: 200, minWidth: 100, type: 'textarea' },
  { key: 'start_name', label: 'Откуда', width: 140, minWidth: 80, type: 'textarea' },
  { key: 'stops_count', label: 'Остановки', width: 80, minWidth: 60, type: 'stops' },
  { key: 'end_name', label: 'Куда', width: 140, minWidth: 80, type: 'textarea' },
  { key: 'bus_number', label: 'Автобус', width: 100, minWidth: 70, type: 'select' },
  { key: 'payment_amount', label: 'Оплата €', width: 90, minWidth: 70, type: 'number' },
  { key: 'driver_salary', label: 'Зарплата €', width: 90, minWidth: 70, type: 'number' },
  { key: 'contact_phone', label: 'Телефон', width: 120, minWidth: 90, type: 'text' },
  { key: 'is_international', label: '🌍', width: 40, minWidth: 40, type: 'checkbox' },
];

// LocalStorage key for column widths
const COLUMN_WIDTHS_KEY = 'busas_excel_column_widths';

// Stops Editor Modal
function StopsEditorModal({ stops, onSave, onClose }) {
  const [localStops, setLocalStops] = useState(stops || []);

  const addStop = () => {
    setLocalStops([...localStops, { name: '', address: '', coords: '', arrival_time: '' }]);
  };

  const updateStop = (index, field, value) => {
    const updated = [...localStops];
    updated[index] = { ...updated[index], [field]: value };
    setLocalStops(updated);
  };

  const removeStop = (index) => {
    setLocalStops(localStops.filter((_, i) => i !== index));
  };

  // Format coords for display
  const formatCoords = (stop) => {
    if (stop.coords) return stop.coords;
    if (stop.lat && stop.lng) return `${stop.lat}, ${stop.lng}`;
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Промежуточные точки ({localStops.length})
          </h3>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 max-h-[60vh] overflow-auto">
          {localStops.length === 0 ? (
            <p className="text-center text-gray-500 py-4">Нет промежуточных точек</p>
          ) : (
            <div className="space-y-3">
              {localStops.map((stop, index) => (
                <div key={index} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-orange-600">Точка {index + 1}</span>
                    <button 
                      onClick={() => removeStop(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={stop.name || ''}
                      onChange={(e) => updateStop(index, 'name', e.target.value)}
                      placeholder="Название"
                      className="border rounded px-2 py-1 text-sm"
                    />
                    <input
                      type="text"
                      value={stop.address || ''}
                      onChange={(e) => updateStop(index, 'address', e.target.value)}
                      placeholder="Адрес"
                      className="border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={formatCoords(stop)}
                      onChange={(e) => updateStop(index, 'coords', e.target.value)}
                      placeholder="Координаты (54.68, 25.27)"
                      className="border rounded px-2 py-1 text-sm bg-blue-50"
                    />
                    <TimeInput24h
                      value={stop.arrival_time || ''}
                      onChange={(val) => updateStop(index, 'arrival_time', val)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t p-3 flex items-center justify-between bg-gray-50">
          <Button size="sm" variant="outline" onClick={addStop}>
            <Plus className="w-4 h-4 mr-1" /> Добавить точку
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onClose}>Отмена</Button>
            <Button size="sm" onClick={() => { onSave(localStops); onClose(); }} className="bg-orange-500 hover:bg-orange-600 text-white">
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile Trip Card Component
function MobileTripCard({ trip, buses, onEdit, onCopy, onDelete, isNew, isModified, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const today = formatDateLocal(new Date());
  const isToday = trip.date === today;
  const isFuture = trip.date > today;
  
  const tripDate = new Date(trip.date);
  const day = tripDate.getDate();
  const month = tripDate.toLocaleDateString('ru-RU', { month: 'short' });
  const weekday = tripDate.toLocaleDateString('ru-RU', { weekday: 'short' });

  return (
    <div className={`rounded-xl shadow-sm border-2 overflow-hidden transition-all ${
      isNew ? 'border-green-400 bg-green-50' : 
      isModified ? 'border-yellow-400 bg-yellow-50' : 
      isToday ? 'border-red-400 bg-red-50' :
      isFuture ? 'border-blue-400 bg-blue-50' :
      'border-gray-200 bg-white'
    }`}>
      {/* Main card content - always visible */}
      <div className="p-3" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-3">
          {/* Date block */}
          <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl font-bold shrink-0 ${
            isToday ? 'bg-red-500 text-white' : 
            isFuture ? 'bg-blue-500 text-white' : 
            'bg-gray-300 text-gray-700'
          }`}>
            <span className="text-xl leading-none">{day}</span>
            <span className="text-[10px] uppercase">{month}</span>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {trip.time_start && (
                <span className="text-lg font-bold text-gray-800">{trip.time_start}</span>
              )}
              {isNew && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-500 text-white">НОВЫЙ</span>}
              {isModified && !isNew && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-yellow-500 text-white">ИЗМЕНЁН</span>}
            </div>
            
            <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
              <MapPinned className="w-3.5 h-3.5 text-green-600 shrink-0" />
              <span className="truncate">{trip.start_name || '—'}</span>
              <span className="text-gray-400">→</span>
              <span className="truncate">{trip.end_name || '—'}</span>
            </div>
            
            {trip.description && (
              <p className="text-xs text-gray-500 line-clamp-1">{trip.description}</p>
            )}
          </div>
          
          {/* Expand indicator */}
          <div className="shrink-0 text-gray-400">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        {/* Quick stats row */}
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200/50 text-sm">
          {trip.bus_number && (
            <span className="flex items-center gap-1 text-gray-600">
              <Bus className="w-3.5 h-3.5" /> {trip.bus_number}
            </span>
          )}
          {trip.driver_salary > 0 && (
            <span className="flex items-center gap-1 text-green-600 font-semibold">
              <Euro className="w-3.5 h-3.5" /> {trip.driver_salary}
            </span>
          )}
          {trip.payment_amount > 0 && (
            <span className="flex items-center gap-1 text-orange-600">
              💵 {trip.payment_amount}
            </span>
          )}
          {trip.stops_count > 0 && (
            <span className="text-purple-600 text-xs">+{trip.stops_count} ост.</span>
          )}
        </div>
      </div>
      
      {/* Expanded details */}
      {expanded && (
        <div className="border-t bg-white/80 p-3 space-y-3">
          {/* Full description */}
          {trip.description && (
            <div className="bg-amber-50 p-2 rounded-lg">
              <p className="text-xs font-medium text-amber-700 mb-1">📝 Описание</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{trip.description}</p>
            </div>
          )}
          
          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Откуда</p>
              <p className="font-medium">{trip.start_name || '—'}</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Куда</p>
              <p className="font-medium">{trip.end_name || '—'}</p>
            </div>
            {trip.contact_phone && (
              <div className="bg-gray-50 p-2 rounded col-span-2">
                <p className="text-xs text-gray-500">Телефон</p>
                <a href={`tel:${trip.contact_phone}`} className="font-medium text-blue-600">{trip.contact_phone}</a>
              </div>
            )}
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Оплата</p>
              <p className="font-medium">{trip.payment_amount || 0} €</p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(trip); }} className="flex-1">
              <Edit className="w-4 h-4 mr-1" /> Изменить
            </Button>
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onCopy(trip); }}>
              <Copy className="w-4 h-4" />
            </Button>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDelete(trip); }} className="text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Edit Modal
function MobileEditModal({ trip, buses, onSave, onClose }) {
  const [formData, setFormData] = useState({ ...trip });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-h-[90vh] rounded-t-2xl sm:rounded-2xl sm:max-w-md overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold">✏️ Редактирование</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-auto p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Дата</label>
              <input
                type="date"
                value={formData.date || ''}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Время</label>
              <TimeInput24h
                value={formData.time_start || ''}
                onChange={(val) => handleChange('time_start', val)}
              />
            </div>
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-1">Описание</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-1">Откуда</label>
            <input
              type="text"
              value={formData.start_name || ''}
              onChange={(e) => handleChange('start_name', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-1">Куда</label>
            <input
              type="text"
              value={formData.end_name || ''}
              onChange={(e) => handleChange('end_name', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-1">Автобус</label>
            <select
              value={formData.bus_number || ''}
              onChange={(e) => handleChange('bus_number', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">-- Не выбран --</option>
              {buses.map(bus => (
                <option key={bus.id} value={bus.license_plate}>{bus.license_plate}</option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Оплата €</label>
              <input
                type="number"
                value={formData.payment_amount || ''}
                onChange={(e) => handleChange('payment_amount', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Зарплата €</label>
              <input
                type="number"
                value={formData.driver_salary || ''}
                onChange={(e) => handleChange('driver_salary', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-1">Телефон</label>
            <input
              type="tel"
              value={formData.contact_phone || ''}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        
        <div className="border-t p-4 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Отмена</Button>
          <Button onClick={() => { onSave(formData); onClose(); }} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
            💾 Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}

// Mobile Excel View Component
function MobileExcelView({ drivers, buses, onClose, isAdmin }) {
  const [selectedDriver, setSelectedDriver] = useState('');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [clipboardTrip, setClipboardTrip] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' - новые вверху

  const fetchTrips = useCallback(async () => {
    if (!selectedDriver) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/trips`, getAuthHeaders());
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      
      const driverTrips = response.data
        .filter(trip => trip.driver_id === selectedDriver && new Date(trip.date) >= twoMonthsAgo)
        .map(trip => ({
          ...trip,
          start_name: trip.start_location?.name || '',
          end_name: trip.end_location?.name || '',
          stops_count: (trip.stops || []).length,
          _modified: false,
          _isNew: false
        }))
        .sort((a, b) => {
          const dateA = a.date || '';
          const dateB = b.date || '';
          const timeA = a.time_start || '';
          const timeB = b.time_start || '';
          if (sortOrder === 'desc') {
            if (dateA !== dateB) return dateB.localeCompare(dateA);
            return timeB.localeCompare(timeA);
          } else {
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return timeA.localeCompare(timeB);
          }
        });
      
      setTrips(driverTrips);
    } catch (error) {
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [selectedDriver, sortOrder]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const handleEdit = (updatedTrip) => {
    setTrips(prev => prev.map(t => 
      (t.id || t._tempId) === (updatedTrip.id || updatedTrip._tempId) 
        ? { ...updatedTrip, _modified: true } 
        : t
    ));
    setHasChanges(true);
  };

  const handleCopy = (trip) => {
    setClipboardTrip(trip);
    toast.success('✓ Скопировано');
  };

  const handlePaste = () => {
    if (!clipboardTrip || !selectedDriver) return;
    const driver = drivers.find(d => d.id === selectedDriver);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const newTrip = {
      ...clipboardTrip,
      date: formatDateLocal(tomorrow),
      _tempId: `paste_${Date.now()}`,
      _isNew: true,
      _modified: true,
      driver_id: selectedDriver,
      driver_name: driver?.full_name || driver?.username || ''
    };
    delete newTrip.id;
    delete newTrip._id;
    
    setTrips(prev => [...prev, newTrip]);
    setHasChanges(true);
    toast.success('✓ Вставлено');
  };

  const handleDelete = (trip) => {
    if (!window.confirm('Удалить этот рейс?')) return;
    
    if (trip._isNew) {
      setTrips(prev => prev.filter(t => t._tempId !== trip._tempId));
    } else {
      // Mark for deletion or delete immediately
      axios.delete(`${API}/trips/${trip.id}`, getAuthHeaders())
        .then(() => {
          setTrips(prev => prev.filter(t => t.id !== trip.id));
          toast.success('Удалено');
        })
        .catch(() => toast.error('Ошибка удаления'));
    }
  };

  const handleAddNew = () => {
    if (!selectedDriver) {
      toast.error('Сначала выберите водителя');
      return;
    }
    const driver = drivers.find(d => d.id === selectedDriver);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const newTrip = {
      _tempId: `new_${Date.now()}`,
      _isNew: true,
      _modified: true,
      date: formatDateLocal(tomorrow),
      time_start: '',
      start_name: '',
      end_name: '',
      description: '',
      bus_number: '',
      payment_amount: 0,
      driver_salary: 0,
      contact_phone: '',
      is_international: false,
      stops_count: 0,
      driver_id: selectedDriver,
      driver_name: driver?.full_name || driver?.username || ''
    };
    
    setTrips(prev => [...prev, newTrip]);
    setHasChanges(true);
    setEditingTrip(newTrip);
  };

  const saveChanges = async () => {
    const modifiedTrips = trips.filter(t => t._modified);
    if (modifiedTrips.length === 0) return;

    setLoading(true);
    try {
      for (const trip of modifiedTrips) {
        const tripData = {
          date: trip.date,
          time_start: trip.time_start,
          time_end: trip.time_end || '',
          route_name: trip.route_name || '',
          start_location: { name: trip.start_name, address: '' },
          end_location: { name: trip.end_name, address: '' },
          stops: trip.stops || [],
          bus_id: '',
          bus_number: trip.bus_number || '',
          driver_id: trip.driver_id,
          driver_name: trip.driver_name,
          payment_amount: parseFloat(trip.payment_amount) || 0,
          driver_salary: parseFloat(trip.driver_salary) || 0,
          contact_phone: trip.contact_phone || '',
          description: trip.description || '',
          is_international: trip.is_international || false,
        };

        if (trip._isNew) {
          await axios.post(`${API}/trips`, tripData, getAuthHeaders());
        } else {
          await axios.put(`${API}/trips/${trip.id}`, tripData, getAuthHeaders());
        }
      }
      toast.success(`✓ Сохранено ${modifiedTrips.length} рейс(ов)`);
      setHasChanges(false);
      fetchTrips();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const driverName = drivers.find(d => d.id === selectedDriver)?.full_name || 
                     drivers.find(d => d.id === selectedDriver)?.username || '';

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <span className="text-xl">📊</span>
          <span className="font-bold">{driverName || 'Excel'}</span>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Toolbar */}
      <div className="bg-white px-3 py-2 flex items-center gap-2 border-b shadow-sm overflow-x-auto">
        <select
          value={selectedDriver}
          onChange={(e) => setSelectedDriver(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm min-w-[140px]"
        >
          <option value="">-- Водитель --</option>
          {drivers.map(driver => (
            <option key={driver.id} value={driver.id}>
              {driver.full_name || driver.username}
            </option>
          ))}
        </select>
        
        <Button size="sm" variant="outline" onClick={handleAddNew} className="shrink-0">
          <Plus className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={handlePaste} disabled={!clipboardTrip} className="shrink-0">
          <ClipboardPaste className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={fetchTrips} className="shrink-0">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')} 
          className="shrink-0"
          title={sortOrder === 'desc' ? 'Новые вверху' : 'Старые вверху'}
        >
          {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </Button>
        
        <div className="flex-1" />
        
        {hasChanges && (
          <Button size="sm" onClick={saveChanges} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white shrink-0 animate-pulse">
            <Save className="w-4 h-4 mr-1" /> Сохранить
          </Button>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-auto p-3 space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Загрузка...</div>
        ) : !selectedDriver ? (
          <div className="text-center py-8 text-gray-500">
            <span className="text-4xl mb-2 block">👆</span>
            Выберите водителя
          </div>
        ) : trips.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <span className="text-4xl mb-2 block">📭</span>
            Нет рейсов
          </div>
        ) : (
          trips.map(trip => (
            <MobileTripCard
              key={trip.id || trip._tempId}
              trip={trip}
              buses={buses}
              onEdit={setEditingTrip}
              onCopy={handleCopy}
              onDelete={handleDelete}
              isNew={trip._isNew}
              isModified={trip._modified && !trip._isNew}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>
      
      {/* Status bar */}
      {selectedDriver && (
        <div className="bg-white border-t px-4 py-2 text-sm text-gray-500 flex items-center justify-between">
          <span>{trips.length} рейс(ов)</span>
          {clipboardTrip && <span className="text-blue-600">📋 Буфер: 1</span>}
        </div>
      )}
      
      {/* Edit Modal */}
      {editingTrip && (
        <MobileEditModal
          trip={editingTrip}
          buses={buses}
          onSave={handleEdit}
          onClose={() => setEditingTrip(null)}
        />
      )}
    </div>
  );
}

// Single Excel Window Component
function ExcelWindow({ 
  id, 
  drivers, 
  buses, 
  onClose, 
  initialPosition,
  clipboardRow,
  onCopyRow,
  zIndex,
  onFocus,
  isActive,
  registerWindow,
  unregisterWindow,
  isAdmin
}) {
  const [selectedDriver, setSelectedDriver] = useState('');
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { tripId, col }
  const [selectedCell, setSelectedCell] = useState(null); // { tripId, col } - выделенная ячейка (не редактируемая)
  const [cellClipboard, setCellClipboard] = useState(null); // значение скопированной ячейки
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 1400, height: 500 });
  const [hasChanges, setHasChanges] = useState(false);
  const [editingStops, setEditingStops] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    // Try to load saved widths from localStorage
    try {
      const saved = localStorage.getItem(COLUMN_WIDTHS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults (in case new columns added)
        const widths = {};
        DEFAULT_COLUMNS.forEach(col => { 
          widths[col.key] = parsed[col.key] || col.width; 
        });
        return widths;
      }
    } catch (e) {
      console.warn('Failed to load column widths from localStorage');
    }
    // Default widths
    const widths = {};
    DEFAULT_COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' - новые вверху
  const nodeRef = useRef(null);
  const tableRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const colResizeStartRef = useRef({ x: 0, width: 0 });

  // Save column widths to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidths));
    } catch (e) {
      console.warn('Failed to save column widths to localStorage');
    }
  }, [columnWidths]);

  // Keyboard handler for cell copy/paste (Excel-like)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if this window is focused and we have a selected cell
      if (!selectedCell) return;
      
      // Ctrl+C - copy cell value
      if (e.ctrlKey && e.key === 'c' && !editingCell) {
        const trip = filteredTrips.find(t => (t.id || t._tempId) === selectedCell.tripId);
        if (trip) {
          const value = trip[selectedCell.col] || '';
          setCellClipboard({ col: selectedCell.col, value: String(value) });
          // Also copy to system clipboard
          navigator.clipboard.writeText(String(value)).catch(() => {});
          toast.success('Скопировано');
          e.preventDefault();
        }
      }
      
      // Ctrl+V - paste to cell
      if (e.ctrlKey && e.key === 'v' && !editingCell) {
        e.preventDefault();
        // Try to read from system clipboard first
        navigator.clipboard.readText().then(text => {
          if (text) {
            handleCellChange(selectedCell.tripId, selectedCell.col, text);
            toast.success('Вставлено');
          }
        }).catch(() => {
          // Fallback to internal clipboard
          if (cellClipboard) {
            handleCellChange(selectedCell.tripId, selectedCell.col, cellClipboard.value);
            toast.success('Вставлено');
          }
        });
      }
      
      // Delete/Backspace - clear cell
      if ((e.key === 'Delete' || e.key === 'Backspace') && !editingCell) {
        handleCellChange(selectedCell.tripId, selectedCell.col, '');
        e.preventDefault();
      }
      
      // Enter - start editing
      if (e.key === 'Enter' && !editingCell) {
        setEditingCell(selectedCell);
        e.preventDefault();
      }
      
      // Arrow keys - navigate cells
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !editingCell) {
        e.preventDefault();
        const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === selectedCell.tripId);
        const currentColIndex = DEFAULT_COLUMNS.findIndex(c => c.key === selectedCell.col);
        
        let newTripIndex = currentTripIndex;
        let newColIndex = currentColIndex;
        
        if (e.key === 'ArrowUp' && currentTripIndex > 0) newTripIndex--;
        if (e.key === 'ArrowDown' && currentTripIndex < filteredTrips.length - 1) newTripIndex++;
        if (e.key === 'ArrowLeft' && currentColIndex > 0) newColIndex--;
        if (e.key === 'ArrowRight' && currentColIndex < DEFAULT_COLUMNS.length - 1) newColIndex++;
        
        const newTrip = filteredTrips[newTripIndex];
        const newCol = DEFAULT_COLUMNS[newColIndex];
        if (newTrip && newCol) {
          setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: newCol.key });
        }
      }
      
      // Tab - move to next cell in row, Shift+Tab - move to previous
      // Only handle Tab when not editing (local handlers in inputs handle Tab during edit)
      if (e.key === 'Tab' && !editingCell) {
        e.preventDefault();
        const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === selectedCell.tripId);
        const currentColIndex = DEFAULT_COLUMNS.findIndex(c => c.key === selectedCell.col);
        
        let newColIndex = currentColIndex;
        let newTripIndex = currentTripIndex;
        
        if (e.shiftKey) {
          // Shift+Tab - previous cell
          newColIndex--;
          // Skip stops column
          if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
          if (newColIndex < 0) {
            // Move to previous row, last column
            newColIndex = DEFAULT_COLUMNS.length - 1;
            newTripIndex--;
          }
        } else {
          // Tab - next cell
          newColIndex++;
          // Skip stops column
          if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
          if (newColIndex >= DEFAULT_COLUMNS.length) {
            // Move to next row, first column
            newColIndex = 0;
            newTripIndex++;
          }
        }
        
        if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && newColIndex >= 0 && newColIndex < DEFAULT_COLUMNS.length) {
          const newTrip = filteredTrips[newTripIndex];
          const finalCol = DEFAULT_COLUMNS[newColIndex];
          if (newTrip && finalCol) {
            setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: finalCol.key });
            setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: finalCol.key });
          }
        }
      }
      
      // Escape - deselect
      if (e.key === 'Escape') {
        setSelectedCell(null);
        setEditingCell(null);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, editingCell, filteredTrips, cellClipboard]);

  // Register this window's copy/paste handlers with parent
  useEffect(() => {
    const handlers = {
      getSelectedTrip: () => {
        if (selectedRow !== null && filteredTrips[selectedRow]) {
          return filteredTrips[selectedRow];
        }
        return null;
      },
      paste: (rowData) => {
        if (!selectedDriver) {
          toast.error('Сначала выберите водителя');
          return false;
        }
        const driver = drivers.find(d => d.id === selectedDriver);
        // Завтрашняя дата
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = formatDateLocal(tomorrow);
        
        const newTrip = {
          ...rowData,
          date: tomorrowStr,
          stops: [...(rowData.stops || [])],
          stops_count: (rowData.stops || []).length,
          _tempId: `paste_${Date.now()}`,
          _isNew: true,
          _modified: true,
          driver_id: selectedDriver,
          driver_name: driver?.full_name || driver?.username || ''
        };
        setTrips(prev => [...prev, newTrip]);
        setHasChanges(true);
        return true;
      }
    };
    registerWindow(id, handlers);
    return () => unregisterWindow(id);
  }, [id, selectedRow, filteredTrips, selectedDriver, drivers, registerWindow, unregisterWindow]);

  // Fetch trips for selected driver
  const fetchTrips = useCallback(async () => {
    if (!selectedDriver) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/trips`, getAuthHeaders());
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      const twoMonthsAgoStr = formatDateLocal(twoMonthsAgo);
      
      const driverTrips = response.data
        .filter(trip => trip.driver_id === selectedDriver && trip.date >= twoMonthsAgoStr)
        .map(trip => ({
          ...trip,
          start_name: trip.start_location?.name || '',
          start_coords: trip.start_location?.lat && trip.start_location?.lng 
            ? `${trip.start_location.lat}, ${trip.start_location.lng}` : '',
          end_name: trip.end_location?.name || '',
          end_coords: trip.end_location?.lat && trip.end_location?.lng 
            ? `${trip.end_location.lat}, ${trip.end_location.lng}` : '',
          stops: (trip.stops || []).map(s => ({
            ...s,
            coords: s.lat && s.lng ? `${s.lat}, ${s.lng}` : (s.coords || '')
          })),
          stops_count: (trip.stops || []).length,
          _original: { ...trip },
          _modified: false,
          _isNew: false
        }))
        .sort((a, b) => {
          const dateA = a.date || '';
          const dateB = b.date || '';
          const timeA = a.time_start || '';
          const timeB = b.time_start || '';
          // desc = новые вверху (сравниваем B с A), asc = старые вверху (сравниваем A с B)
          if (sortOrder === 'desc') {
            if (dateA !== dateB) return dateB.localeCompare(dateA);
            return timeB.localeCompare(timeA);
          } else {
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return timeA.localeCompare(timeB);
          }
        });
      
      setTrips(driverTrips);
      setFilteredTrips(driverTrips);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Ошибка загрузки рейсов');
    } finally {
      setLoading(false);
    }
  }, [selectedDriver, sortOrder]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Apply filters
  useEffect(() => {
    let result = [...trips];
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (key === 'date') {
          // Date filter: match date AND also consider time for sorting
          result = result.filter(trip => {
            const cellValue = String(trip.date || '').toLowerCase();
            return cellValue.includes(value.toLowerCase());
          });
        } else {
          result = result.filter(trip => {
            const cellValue = String(trip[key] || '').toLowerCase();
            return cellValue.includes(value.toLowerCase());
          });
        }
      }
    });
    
    // Sort by date and time according to sortOrder
    // New rows: при desc - вверху, при asc - внизу
    // Modified rows: остаются на месте (не сортируются) до сохранения
    result.sort((a, b) => {
      // New rows: при desc ставим вверх, при asc - вниз
      if (a._isNew && !b._isNew) return sortOrder === 'desc' ? -1 : 1;
      if (!a._isNew && b._isNew) return sortOrder === 'desc' ? 1 : -1;
      // Обе новые - сортируем по времени создания (последняя вверху при desc)
      if (a._isNew && b._isNew) {
        const createdA = a._createdAt || 0;
        const createdB = b._createdAt || 0;
        return sortOrder === 'desc' ? createdB - createdA : createdA - createdB;
      }
      
      // Modified rows: остаются на месте до сохранения (return 0 сохраняет текущий порядок)
      if (a._modified || b._modified) return 0;
      
      const dateA = a.date || '';
      const dateB = b.date || '';
      const timeA = a.time_start || '';
      const timeB = b.time_start || '';
      
      // desc = новые вверху, asc = старые вверху
      if (sortOrder === 'desc') {
        if (dateA !== dateB) return dateB.localeCompare(dateA);
        return timeB.localeCompare(timeA);
      } else {
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return timeA.localeCompare(timeB);
      }
    });
    
    setFilteredTrips(result);
  }, [trips, filters, sortOrder]);

  // Handle cell edit - use trip id instead of index
  const handleCellChange = (tripId, column, value) => {
    setTrips(prevTrips => {
      const updatedTrips = [...prevTrips];
      const actualIndex = updatedTrips.findIndex(t => (t.id || t._tempId) === tripId);
      
      if (actualIndex !== -1) {
        // Support updating multiple fields at once
        if (typeof column === 'object') {
          updatedTrips[actualIndex] = {
            ...updatedTrips[actualIndex],
            ...column,
            _modified: true
          };
        } else {
          updatedTrips[actualIndex] = {
            ...updatedTrips[actualIndex],
            [column]: value,
            _modified: true
          };
        }
      }
      return updatedTrips;
    });
    setHasChanges(true);
  };

  // Update stops for a trip - use trip id
  const handleUpdateStops = (tripId, newStops) => {
    const updatedTrips = [...trips];
    const actualIndex = trips.findIndex(t => (t.id || t._tempId) === tripId);
    
    if (actualIndex !== -1) {
      updatedTrips[actualIndex] = {
        ...updatedTrips[actualIndex],
        stops: newStops,
        stops_count: newStops.length,
        _modified: true
      };
      setTrips(updatedTrips);
      setHasChanges(true);
    }
  };

  // Add new row
  const addNewRow = () => {
    if (!selectedDriver) {
      toast.error('Сначала выберите водителя');
      return;
    }
    
    const driver = drivers.find(d => d.id === selectedDriver);
    // Завтрашняя дата
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = formatDateLocal(tomorrow);
    
    const newTrip = {
      _tempId: `new_${Date.now()}`,
      _isNew: true,
      _modified: true,
      _createdAt: Date.now(), // Для сортировки между новыми строками
      date: tomorrowStr,
      time_start: '',
      time_end: '',
      route_name: '',
      start_name: '',
      start_coords: '',
      end_name: '',
      end_coords: '',
      stops: [],
      stops_count: 0,
      bus_number: '',
      bus_id: '',
      payment_amount: 0,
      driver_salary: 0,
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      description: '',
      driver_id: selectedDriver,
      driver_name: driver?.full_name || driver?.username || ''
    };
    
    // Добавляем в начало при desc, в конец при asc
    if (sortOrder === 'desc') {
      setTrips([newTrip, ...trips]);
    } else {
      setTrips([...trips, newTrip]);
    }
    setHasChanges(true);
    toast.success('Новая строка добавлена');
  };

  // Copy row to clipboard
  const copyRowToClipboard = (trip) => {
    const rowData = { 
      ...trip,
      stops: [...(trip.stops || [])]
    };
    delete rowData.id;
    delete rowData._id;
    delete rowData._tempId;
    delete rowData._original;
    delete rowData._modified;
    delete rowData._isNew;
    delete rowData.driver_id;
    delete rowData.driver_name;
    delete rowData.bus_id;  // Очищаем bus_id при копировании
    onCopyRow(rowData);
    toast.success('✓ Скопировано! Нажмите Ctrl+V для вставки');
  };

  // Auto distribute trips without driver among available drivers
  const autoDistributeTrips = () => {
    // Find trips without driver
    const tripsWithoutDriver = trips.filter(t => !t.driver_id || !t.driver_name);
    
    if (tripsWithoutDriver.length === 0) {
      toast.info('Все рейсы уже имеют водителя');
      return;
    }
    
    if (drivers.length === 0) {
      toast.error('Нет доступных водителей');
      return;
    }
    
    // Group trips by date
    const tripsByDate = {};
    tripsWithoutDriver.forEach(trip => {
      if (!tripsByDate[trip.date]) {
        tripsByDate[trip.date] = [];
      }
      tripsByDate[trip.date].push(trip);
    });
    
    // Count current trips per driver per date
    const driverLoadByDate = {};
    trips.forEach(trip => {
      if (trip.driver_id) {
        const key = `${trip.date}_${trip.driver_id}`;
        driverLoadByDate[key] = (driverLoadByDate[key] || 0) + 1;
      }
    });
    
    // Distribute trips
    const updatedTrips = [...trips];
    let distributed = 0;
    
    Object.keys(tripsByDate).forEach(date => {
      const dateTrips = tripsByDate[date];
      
      dateTrips.forEach(tripToAssign => {
        // Find driver with least trips on this date
        let bestDriver = null;
        let minLoad = Infinity;
        
        drivers.forEach(driver => {
          const key = `${date}_${driver.id}`;
          const load = driverLoadByDate[key] || 0;
          if (load < minLoad) {
            minLoad = load;
            bestDriver = driver;
          }
        });
        
        if (bestDriver) {
          // Update the trip
          const tripIndex = updatedTrips.findIndex(t => 
            (t.id || t._tempId) === (tripToAssign.id || tripToAssign._tempId)
          );
          
          if (tripIndex !== -1) {
            updatedTrips[tripIndex] = {
              ...updatedTrips[tripIndex],
              driver_id: bestDriver.id,
              driver_name: bestDriver.full_name || bestDriver.username,
              _modified: true
            };
            
            // Update load counter
            const key = `${date}_${bestDriver.id}`;
            driverLoadByDate[key] = (driverLoadByDate[key] || 0) + 1;
            distributed++;
          }
        }
      });
    });
    
    if (distributed > 0) {
      setTrips(updatedTrips);
      setHasChanges(true);
      toast.success(`Распределено ${distributed} рейсов по водителям`);
    }
  };

  // Paste row from clipboard (button click)
  const pasteFromClipboard = () => {
    if (!clipboardRow) {
      toast.error('Буфер пуст. Выделите строку и нажмите кнопку копирования');
      return;
    }
    if (!selectedDriver) {
      toast.error('Сначала выберите водителя');
      return;
    }
    
    const driver = drivers.find(d => d.id === selectedDriver);
    // Завтрашняя дата
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = formatDateLocal(tomorrow);
    
    const newTrip = {
      ...clipboardRow,
      date: tomorrowStr,
      stops: [...(clipboardRow.stops || [])],
      stops_count: (clipboardRow.stops || []).length,
      _tempId: `paste_${Date.now()}`,
      _isNew: true,
      _modified: true,
      driver_id: selectedDriver,
      driver_name: driver?.full_name || driver?.username || ''
    };
    
    setTrips([...trips, newTrip]);
    setHasChanges(true);
    toast.success('✓ Вставлено из буфера');
  };

  // Save all changes
  // Parse coordinates string "lat, lng" to object
  const parseCoords = (coordsStr) => {
    if (!coordsStr) return { lat: null, lng: null };
    const parts = coordsStr.split(',').map(s => s.trim());
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return { lat: null, lng: null };
  };

  const saveChanges = async () => {
    const modifiedTrips = trips.filter(t => t._modified);
    
    if (modifiedTrips.length === 0) {
      toast.info('Нет изменений для сохранения');
      return;
    }

    setLoading(true);
    let savedCount = 0;
    let errorCount = 0;

    for (const trip of modifiedTrips) {
      try {
        const bus = buses.find(b => b.license_plate === trip.bus_number);
        const startCoords = parseCoords(trip.start_coords);
        const endCoords = parseCoords(trip.end_coords);
        
        // Parse stops coordinates
        const parsedStops = (trip.stops || []).map(stop => {
          const coords = parseCoords(stop.coords);
          return {
            name: stop.name || '',
            address: stop.address || '',
            lat: coords.lat,
            lng: coords.lng,
            arrival_time: stop.arrival_time || ''
          };
        });
        
        const tripData = {
          date: trip.date,
          time_start: trip.time_start,
          time_end: trip.time_end,
          route_name: trip.route_name,
          start_location: { name: trip.start_name, address: '', lat: startCoords.lat, lng: startCoords.lng },
          end_location: { name: trip.end_name, address: '', lat: endCoords.lat, lng: endCoords.lng },
          stops: parsedStops,
          bus_id: trip.bus_number ? (bus?.id || trip.bus_id || '') : '',
          bus_number: trip.bus_number || '',
          driver_id: trip.driver_id,
          driver_name: trip.driver_name,
          payment_amount: parseFloat(trip.payment_amount) || 0,
          driver_salary: parseFloat(trip.driver_salary) || 0,
          contact_name: trip.contact_name || '',
          contact_phone: trip.contact_phone || '',
          contact_email: trip.contact_email || '',
          description: trip.description || '',
          is_international: trip.is_international || false,
          status: trip.status || 'scheduled'
        };

        if (trip._isNew) {
          await axios.post(`${API}/trips`, tripData, getAuthHeaders());
        } else {
          await axios.put(`${API}/trips/${trip.id}`, tripData, getAuthHeaders());
        }
        savedCount++;
      } catch (error) {
        console.error('Error saving trip:', error);
        errorCount++;
      }
    }

    setLoading(false);
    
    if (savedCount > 0) {
      toast.success(`✓ Сохранено: ${savedCount} рейс(ов)`);
      setHasChanges(false);
      fetchTrips();
    }
    if (errorCount > 0) {
      toast.error(`Ошибок: ${errorCount}`);
    }
  };

  // Delete row
  const deleteRow = async (trip, e) => {
    e?.stopPropagation();
    if (!window.confirm('Удалить этот рейс?')) return;
    
    if (trip._isNew) {
      setTrips(trips.filter(t => t._tempId !== trip._tempId));
      return;
    }

    try {
      await axios.delete(`${API}/trips/${trip.id}`, getAuthHeaders());
      toast.success('Рейс удалён');
      fetchTrips();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Ошибка удаления');
    }
  };

  // Get trip identifier
  const getTripId = (trip) => trip.id || trip._tempId;

  // Render cell
  const renderCell = (trip, column) => {
    const tripId = getTripId(trip);
    const isEditing = editingCell?.tripId === tripId && editingCell?.col === column.key;
    const value = trip[column.key] ?? '';

    // Special handling for stops column
    if (column.type === 'stops') {
      const count = trip.stops?.length || 0;
      return (
        <div
          className="w-full h-full px-1 py-0.5 cursor-pointer flex items-center justify-center"
          onClick={() => setEditingStops({ tripId, stops: trip.stops || [] })}
        >
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
            {count > 0 ? `${count} ост.` : '+'}
          </span>
        </div>
      );
    }

    if (isEditing) {
      if (column.key === 'bus_number') {
        const handleBusTab = (e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === tripId);
            const colIndex = DEFAULT_COLUMNS.findIndex(c => c.key === column.key);
            
            let newColIndex = colIndex;
            let newTripIndex = currentTripIndex;
            
            if (e.shiftKey) {
              newColIndex--;
              if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
              if (newColIndex < 0) {
                newColIndex = DEFAULT_COLUMNS.length - 1;
                newTripIndex--;
              }
            } else {
              newColIndex++;
              if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
              if (newColIndex >= DEFAULT_COLUMNS.length) {
                newColIndex = 0;
                newTripIndex++;
              }
            }
            
            const nextCol = DEFAULT_COLUMNS[newColIndex];
            if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && nextCol) {
              const newTrip = filteredTrips[newTripIndex];
              setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
              setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
            } else {
              setEditingCell(null);
            }
          }
          if (e.key === 'Escape') setEditingCell(null);
        };
        
        return (
          <div className="flex items-center gap-1 bg-blue-50 px-1">
            <select
              autoFocus
              value={value || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                handleCellChange(tripId, { bus_number: newValue, bus_id: '' });
                setEditingCell(null);
              }}
              onKeyDown={handleBusTab}
              className="flex-1 border-0 bg-transparent text-sm p-0"
            >
              <option value="">--</option>
              {buses.map(bus => (
                <option key={bus.id} value={bus.license_plate}>{bus.license_plate}</option>
              ))}
            </select>
            {value && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCellChange(tripId, { bus_number: '', bus_id: '' });
                  setEditingCell(null);
                }}
                className="text-red-500 hover:text-red-700 text-xs font-bold px-1"
                title="Очистить"
              >
                ✕
              </button>
            )}
          </div>
        );
      }

      // Time input with 24-hour format
      if (column.type === 'time') {
        const [hours, minutes] = (value || '').split(':');
        
        const handleTimeTab = (e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === tripId);
            const colIndex = DEFAULT_COLUMNS.findIndex(c => c.key === column.key);
            
            let newColIndex = colIndex;
            let newTripIndex = currentTripIndex;
            
            if (e.shiftKey) {
              newColIndex--;
              if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
              if (newColIndex < 0) {
                newColIndex = DEFAULT_COLUMNS.length - 1;
                newTripIndex--;
              }
            } else {
              newColIndex++;
              if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
              if (newColIndex >= DEFAULT_COLUMNS.length) {
                newColIndex = 0;
                newTripIndex++;
              }
            }
            
            const nextCol = DEFAULT_COLUMNS[newColIndex];
            if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && nextCol) {
              const newTrip = filteredTrips[newTripIndex];
              setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
              setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
            } else {
              setEditingCell(null);
            }
          }
          if (e.key === 'Escape') setEditingCell(null);
        };
        
        return (
          <div className="flex items-center gap-0.5 bg-blue-50 px-1">
            <select
              autoFocus
              value={hours || ''}
              onChange={(e) => {
                const newTime = `${e.target.value}:${minutes || '00'}`;
                handleCellChange(tripId, column.key, newTime);
              }}
              onKeyDown={handleTimeTab}
              className="border-0 bg-transparent text-sm p-0 w-10"
            >
              <option value="">--</option>
              {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="text-gray-500">:</span>
            <select
              value={minutes || ''}
              onChange={(e) => {
                const newTime = `${hours || '00'}:${e.target.value}`;
                handleCellChange(tripId, column.key, newTime);
              }}
              onBlur={() => setEditingCell(null)}
              onKeyDown={handleTimeTab}
              className="border-0 bg-transparent text-sm p-0 w-10"
            >
              <option value="">--</option>
              {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        );
      }

      // Textarea for description
      if (column.type === 'textarea') {
        const handleTextareaTab = (isShiftKey) => {
          const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === tripId);
          const colIndex = DEFAULT_COLUMNS.findIndex(c => c.key === column.key);
          
          let newColIndex = colIndex;
          let newTripIndex = currentTripIndex;
          
          if (isShiftKey) {
            newColIndex--;
            if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
            if (newColIndex < 0) {
              newColIndex = DEFAULT_COLUMNS.length - 1;
              newTripIndex--;
            }
          } else {
            newColIndex++;
            if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
            if (newColIndex >= DEFAULT_COLUMNS.length) {
              newColIndex = 0;
              newTripIndex++;
            }
          }
          
          const nextCol = DEFAULT_COLUMNS[newColIndex];
          if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && nextCol) {
            const newTrip = filteredTrips[newTripIndex];
            setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
            setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
          } else {
            setEditingCell(null);
          }
        };
        
        return (
          <EditableTextarea
            value={value}
            onChange={(newValue) => handleCellChange(tripId, column.key, newValue)}
            onBlur={() => {}}
            onFinish={() => setEditingCell(null)}
            onTab={handleTextareaTab}
          />
        );
      }

      // Checkbox for boolean fields like is_international
      if (column.type === 'checkbox') {
        return (
          <div className="w-full h-full flex items-center justify-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleCellChange(tripId, column.key, e.target.checked)}
              className="w-4 h-4 accent-purple-600 cursor-pointer"
              title={value ? 'Заграница' : 'Литва'}
            />
          </div>
        );
      }

      // Text input with local state to preserve cursor position
      if (column.type === 'text') {
        const handleTextTab = (isShiftKey) => {
          const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === tripId);
          const colIndex = DEFAULT_COLUMNS.findIndex(c => c.key === column.key);
          
          let newColIndex = colIndex;
          let newTripIndex = currentTripIndex;
          
          if (isShiftKey) {
            newColIndex--;
            if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
            if (newColIndex < 0) {
              newColIndex = DEFAULT_COLUMNS.length - 1;
              newTripIndex--;
            }
          } else {
            newColIndex++;
            if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
            if (newColIndex >= DEFAULT_COLUMNS.length) {
              newColIndex = 0;
              newTripIndex++;
            }
          }
          
          const nextCol = DEFAULT_COLUMNS[newColIndex];
          if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && nextCol) {
            const newTrip = filteredTrips[newTripIndex];
            setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
            setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
          } else {
            setEditingCell(null);
          }
        };
        
        return (
          <EditableInput
            value={value}
            onChange={(newValue) => handleCellChange(tripId, column.key, newValue)}
            onFinish={() => setEditingCell(null)}
            onTab={handleTextTab}
          />
        );
      }

      return (
        <input
          type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
          autoFocus
          value={value}
          onChange={(e) => handleCellChange(tripId, column.key, e.target.value)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setEditingCell(null);
            if (e.key === 'Tab') {
              e.preventDefault();
              const currentTripIndex = filteredTrips.findIndex(t => (t.id || t._tempId) === tripId);
              const colIndex = DEFAULT_COLUMNS.findIndex(c => c.key === column.key);
              
              let newColIndex = colIndex;
              let newTripIndex = currentTripIndex;
              
              if (e.shiftKey) {
                newColIndex--;
                if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex--;
                if (newColIndex < 0) {
                  newColIndex = DEFAULT_COLUMNS.length - 1;
                  newTripIndex--;
                }
              } else {
                newColIndex++;
                if (DEFAULT_COLUMNS[newColIndex]?.type === 'stops') newColIndex++;
                if (newColIndex >= DEFAULT_COLUMNS.length) {
                  newColIndex = 0;
                  newTripIndex++;
                }
              }
              
              const nextCol = DEFAULT_COLUMNS[newColIndex];
              
              if (newTripIndex >= 0 && newTripIndex < filteredTrips.length && nextCol) {
                const newTrip = filteredTrips[newTripIndex];
                setSelectedCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
                setEditingCell({ tripId: newTrip.id || newTrip._tempId, col: nextCol.key });
              } else {
                setEditingCell(null);
              }
            }
            if (e.key === 'Escape') setEditingCell(null);
          }}
          className="w-full h-full border-0 bg-blue-50 text-sm px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          step={column.type === 'number' ? '0.01' : undefined}
        />
      );
    }

    // Display for textarea - allow wrapping
    if (column.type === 'textarea') {
      return (
        <div
          className="w-full h-full min-h-[28px] px-1 py-1 text-sm whitespace-pre-wrap break-words"
          style={{ minHeight: '28px' }}
        >
          {value || '—'}
        </div>
      );
    }

    // Display checkbox - always show as checkbox
    if (column.type === 'checkbox') {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleCellChange(tripId, column.key, e.target.checked)}
            className="w-4 h-4 accent-purple-600 cursor-pointer"
            title={value ? 'Заграница' : 'Литва'}
          />
        </div>
      );
    }

    return (
      <div
        className="w-full h-full min-h-[28px] px-1 py-1 text-sm flex items-center"
      >
        <span className="truncate">{column.type === 'number' ? (value || 0) : (value || '—')}</span>
      </div>
    );
  };

  // Handle column resize
  const handleColumnResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(colKey);
    colResizeStartRef.current = { x: e.clientX, width: columnWidths[colKey] };

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - colResizeStartRef.current.x;
      const col = DEFAULT_COLUMNS.find(c => c.key === colKey);
      const newWidth = Math.max(col?.minWidth || 50, colResizeStartRef.current.width + delta);
      setColumnWidths(prev => ({ ...prev, [colKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const driverName = drivers.find(d => d.id === selectedDriver)?.full_name || 
                     drivers.find(d => d.id === selectedDriver)?.username || 
                     'Не выбран';

  // Цвета для разных окон
  const windowColors = [
    { bg: 'from-blue-600 to-blue-700', border: 'border-blue-500', inactive: 'from-blue-400 to-blue-500' },
    { bg: 'from-emerald-600 to-emerald-700', border: 'border-emerald-500', inactive: 'from-emerald-400 to-emerald-500' },
    { bg: 'from-purple-600 to-purple-700', border: 'border-purple-500', inactive: 'from-purple-400 to-purple-500' },
    { bg: 'from-orange-600 to-orange-700', border: 'border-orange-500', inactive: 'from-orange-400 to-orange-500' },
    { bg: 'from-pink-600 to-pink-700', border: 'border-pink-500', inactive: 'from-pink-400 to-pink-500' },
    { bg: 'from-cyan-600 to-cyan-700', border: 'border-cyan-500', inactive: 'from-cyan-400 to-cyan-500' },
  ];
  const colorIndex = Math.abs(parseInt(id.replace(/\D/g, '') || '0')) % windowColors.length;
  const windowColor = windowColors[colorIndex] || windowColors[0];

  // Статистика
  const modifiedCount = trips.filter(t => t._modified).length;
  const newCount = trips.filter(t => t._isNew).length;

  // Handle resize start
  const handleResizeStart = (e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    onFocus(id);
    
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: windowSize.width,
      height: windowSize.height,
      corner
    };

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - resizeStartRef.current.x;
      const deltaY = moveEvent.clientY - resizeStartRef.current.y;
      const { corner, width, height } = resizeStartRef.current;

      let newWidth = width;
      let newHeight = height;

      if (corner.includes('e')) newWidth = Math.max(600, width + deltaX);
      if (corner.includes('w')) newWidth = Math.max(600, width - deltaX);
      if (corner.includes('s')) newHeight = Math.max(300, height + deltaY);
      if (corner.includes('n')) newHeight = Math.max(300, height - deltaY);

      setWindowSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle"
      defaultPosition={initialPosition}
      bounds="parent"
      onStart={() => onFocus(id)}
      disabled={isResizing}
    >
      <div
        ref={nodeRef}
        className={`absolute rounded-xl overflow-hidden transition-all duration-200 ${
          isActive 
            ? `shadow-2xl ring-2 ring-offset-2 ${windowColor.border} ring-opacity-50` 
            : 'shadow-lg opacity-90 hover:opacity-100'
        }`}
        style={{ 
          width: isMinimized ? 280 : windowSize.width, 
          height: isMinimized ? 'auto' : windowSize.height,
          zIndex,
          minHeight: isMinimized ? 'auto' : 200,
          background: 'white'
        }}
        onClick={() => onFocus(id)}
      >
        {/* Header with color */}
        <div className={`drag-handle bg-gradient-to-r ${isActive ? windowColor.bg : windowColor.inactive} text-white px-3 py-2 flex items-center justify-between cursor-move select-none`}>
          <div className="flex items-center gap-2 min-w-0">
            <GripHorizontal className="w-4 h-4 opacity-50 shrink-0" />
            <span className="font-bold text-sm truncate">
              {selectedDriver ? `📊 ${driverName}` : '👤 Выберите водителя'}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Статистика */}
            {(modifiedCount > 0 || newCount > 0) && (
              <div className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                {newCount > 0 && <span className="text-green-200">+{newCount}</span>}
                {modifiedCount > 0 && <span className="text-yellow-200">✎{modifiedCount}</span>}
              </div>
            )}
            {hasChanges && <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" title="Несохранённые изменения" />}
            <button onClick={() => setIsMinimized(!isMinimized)} className="p-1 hover:bg-white/20 rounded transition-colors">
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button onClick={() => onClose(id)} className="p-1 hover:bg-red-500 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Minimized state shows trip count */}
        {isMinimized && selectedDriver && (
          <div className="px-3 py-2 bg-gray-50 text-sm text-gray-600">
            Рейсов: {trips.length} {hasChanges && <span className="text-yellow-600">• несохранено</span>}
          </div>
        )}

        {!isMinimized && (
          <>
            {/* Toolbar */}
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 px-2 py-2 flex items-center gap-2 border-b flex-wrap">
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white min-w-[150px] shadow-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
              >
                <option value="">-- Водитель --</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.full_name || driver.username}
                  </option>
                ))}
              </select>

              <div className="h-6 w-px bg-gray-300" />

              <Button size="sm" variant="outline" onClick={addNewRow} className="h-8 text-xs shadow-sm hover:shadow" title="Добавить строку">
                <Plus className="w-3.5 h-3.5 mr-1" /> Новый
              </Button>
              <Button size="sm" variant="outline" onClick={pasteFromClipboard} disabled={!clipboardRow} className="h-8 text-xs shadow-sm hover:shadow" title="Вставить">
                <ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Вставить
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={autoDistributeTrips} 
                className="h-8 text-xs shadow-sm hover:shadow bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100" 
                title="Автоматически распределить рейсы без водителя"
              >
                <Users className="w-3.5 h-3.5 mr-1" /> Распределить
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowFilters(!showFilters)} className={`h-8 text-xs shadow-sm ${showFilters ? 'bg-blue-100 border-blue-300' : ''}`}>
                <Filter className="w-3.5 h-3.5 mr-1" /> Фильтры
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchTrips} className="h-8 text-xs" title="Обновить">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')} 
                className="h-8 text-xs" 
                title={sortOrder === 'desc' ? 'Новые вверху' : 'Старые вверху'}
              >
                {sortOrder === 'desc' ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </Button>

              <div className="flex-1" />

              {/* Trip count badge */}
              {selectedDriver && (
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                  {filteredTrips.length} рейс.
                </span>
              )}

              <Button 
                size="sm" 
                onClick={saveChanges} 
                disabled={!hasChanges || loading}
                className={`h-8 text-xs text-white shadow-sm transition-all ${
                  hasChanges 
                    ? 'bg-green-600 hover:bg-green-700 hover:shadow-md animate-pulse' 
                    : 'bg-gray-400'
                }`}
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Сохранить
              </Button>
            </div>

            {/* Table */}
            <div 
              ref={tableRef}
              className="overflow-auto flex-1 bg-white"
              style={{ maxHeight: windowSize.height - 110 }}
            >
              <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
                <thead className="sticky top-0 z-10">
                  {showFilters && (
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 p-0 w-8"></th>
                      {DEFAULT_COLUMNS.map(col => (
                        <th key={col.key} className="border border-gray-300 p-0" style={{ width: columnWidths[col.key] }}>
                          {col.type !== 'stops' && (
                            <input
                              type="text"
                              placeholder="🔍"
                              value={filters[col.key] || ''}
                              onChange={(e) => setFilters({ ...filters, [col.key]: e.target.value })}
                              className="w-full text-xs px-1 py-0.5 border-0 bg-transparent"
                            />
                          )}
                        </th>
                      ))}
                      <th className="border border-gray-300 p-0 w-16"></th>
                    </tr>
                  )}
                  <tr className="bg-gray-200">
                    <th className="border border-gray-300 px-1 py-1 text-xs font-bold w-8">#</th>
                    {DEFAULT_COLUMNS.map(col => (
                      <th 
                        key={col.key} 
                        className="border border-gray-300 px-1 py-1 text-xs font-bold text-left whitespace-nowrap relative group"
                        style={{ width: columnWidths[col.key] }}
                      >
                        {col.label}
                        {/* Column resize handle */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-blue-400 opacity-0 group-hover:opacity-50"
                          onMouseDown={(e) => handleColumnResizeStart(e, col.key)}
                        />
                      </th>
                    ))}
                    <th className="border border-gray-300 px-1 py-1 text-xs font-bold w-16">⚙</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={DEFAULT_COLUMNS.length + 2} className="text-center py-8 text-gray-500">
                        Загрузка...
                      </td>
                    </tr>
                  ) : filteredTrips.length === 0 ? (
                    <tr>
                      <td colSpan={DEFAULT_COLUMNS.length + 2} className="text-center py-8 text-gray-500">
                        {selectedDriver ? 'Нет рейсов за 2 месяца' : 'Выберите водителя'}
                      </td>
                    </tr>
                  ) : (() => {
                    // Создаём карту цветов для дней (чередование по датам)
                    const dateColorMap = {};
                    let colorIndex = 0;
                    let prevDate = null;
                    filteredTrips.forEach(trip => {
                      if (trip.date !== prevDate) {
                        if (prevDate !== null) colorIndex++;
                        dateColorMap[trip.date] = colorIndex % 2 === 0;
                        prevDate = trip.date;
                      }
                    });

                    return filteredTrips.map((trip, rowIndex) => {
                      const isNew = trip._isNew;
                      const isModified = trip._modified && !trip._isNew;
                      const isSelected = selectedRow === rowIndex;
                      const today = formatDateLocal(new Date());
                      const isToday = trip.date === today;
                      const isFuture = trip.date > today;
                      const hasDescription = trip.description && trip.description.length > 30;
                      const isEvenDay = dateColorMap[trip.date] ?? true;

                      return (
                        <tr 
                          key={trip.id || trip._tempId}
                          className={`
                            ${isNew ? 'bg-green-200' : isModified ? 'bg-amber-200' : isEvenDay ? 'bg-white' : 'bg-gray-200'}
                            ${isSelected ? 'ring-2 ring-inset ring-blue-500 !bg-blue-200' : ''}
                            ${isToday ? 'border-l-4 border-l-red-500' : isFuture ? 'border-l-4 border-l-blue-500' : ''}
                            hover:bg-blue-100 cursor-pointer transition-colors
                          `}
                          style={{ verticalAlign: hasDescription ? 'top' : 'middle' }}
                          onClick={() => setSelectedRow(rowIndex)}
                        >
                          <td className={`border border-gray-300 px-1 py-0.5 text-center text-xs text-gray-500 ${!isEvenDay ? 'border-b-gray-400/50' : ''}`}>
                            {rowIndex + 1}
                          </td>
                          {DEFAULT_COLUMNS.map(col => (
                            <td 
                              key={col.key}
                              className={`border border-gray-300 p-0 cursor-cell ${
                              selectedCell?.tripId === getTripId(trip) && selectedCell?.col === col.key 
                                ? 'bg-blue-100 ring-2 ring-blue-500 ring-inset' 
                                : ''
                            }`}
                              style={{ width: columnWidths[col.key], maxWidth: columnWidths[col.key] }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (col.type !== 'stops') {
                                  setSelectedCell({ tripId: getTripId(trip), col: col.key });
                                  setEditingCell({ tripId: getTripId(trip), col: col.key });
                                }
                              }}
                            >
                              {renderCell(trip, col)}
                            </td>
                          ))}
                          <td className="border border-gray-300 px-1 py-0.5">
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); copyRowToClipboard(trip); }}
                                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                                title="Копировать"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={(e) => deleteRow(trip, e)}
                                  className="p-1 hover:bg-red-100 rounded text-red-600"
                                  title="Удалить"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </>
        )}

        {editingStops && (
          <StopsEditorModal
            stops={editingStops.stops}
            onSave={(newStops) => handleUpdateStops(editingStops.tripId, newStops)}
            onClose={() => setEditingStops(null)}
          />
        )}

        {/* Resize handles - styled with window color */}
        {!isMinimized && (
          <>
            {/* Corner resize handle - bottom right (main) */}
            <div
              className={`absolute bottom-0 right-0 w-5 h-5 cursor-se-resize z-20 bg-gradient-to-br ${windowColor.bg} rounded-tl-lg opacity-70 hover:opacity-100 transition-all hover:scale-110 flex items-center justify-center`}
              onMouseDown={(e) => handleResizeStart(e, 'se')}
              title="Изменить размер"
            >
              <svg className="w-3 h-3 text-white/70" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"/>
              </svg>
            </div>
            {/* Edge resize - right */}
            <div
              className="absolute top-14 right-0 w-1.5 bottom-5 cursor-e-resize z-10 hover:bg-blue-400/30 transition-colors rounded-l"
              onMouseDown={(e) => handleResizeStart(e, 'e')}
            />
            {/* Edge resize - bottom */}
            <div
              className="absolute bottom-0 left-5 right-5 h-1.5 cursor-s-resize z-10 hover:bg-blue-400/30 transition-colors rounded-t"
              onMouseDown={(e) => handleResizeStart(e, 's')}
            />
          </>
        )}
      </div>
    </Draggable>
  );
}

// Main Component
export default function TripExcelView({ drivers, buses, onClose, isAdmin }) {
  const isMobile = useIsMobile();
  
  // На мобильных показываем карточный вид
  if (isMobile) {
    return <MobileExcelView drivers={drivers} buses={buses} onClose={onClose} isAdmin={isAdmin} />;
  }
  
  // Desktop версия с окнами
  return <DesktopExcelView drivers={drivers} buses={buses} isAdmin={isAdmin} />;
}

// Desktop Excel View with Windows
function DesktopExcelView({ drivers, buses, isAdmin }) {
  const [windows, setWindows] = useState([]);
  const [clipboardRow, setClipboardRow] = useState(null);
  const [focusedWindow, setFocusedWindow] = useState(null);
  const windowHandlersRef = useRef({});
  const containerRef = useRef(null);

  // Tab key to switch between cells in a row (removed window switching)
  // Window switching is now disabled - Tab works within cells only

  const registerWindow = useCallback((id, handlers) => {
    windowHandlersRef.current[id] = handlers;
  }, []);

  const unregisterWindow = useCallback((id) => {
    delete windowHandlersRef.current[id];
  }, []);

  const openNewWindow = () => {
    const offset = windows.length * 30;
    const newWindow = {
      id: `window_${Date.now()}`,
      position: { x: 50 + offset, y: 50 + offset }
    };
    setWindows([...windows, newWindow]);
    setFocusedWindow(newWindow.id);
  };

  const closeWindow = (id) => {
    setWindows(windows.filter(w => w.id !== id));
    if (focusedWindow === id) {
      setFocusedWindow(windows.length > 1 ? windows.find(w => w.id !== id)?.id : null);
    }
  };

  const handleCopyRow = (rowData) => {
    setClipboardRow(rowData);
  };

  const handleFocus = (id) => {
    setFocusedWindow(id);
  };

  const getZIndex = (id) => {
    return id === focusedWindow ? 1000 : 100;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top toolbar */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 flex items-center gap-4">
        <Button 
          onClick={openNewWindow}
          className="bg-green-600 hover:bg-green-700 text-white font-bold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Открыть окно рейсов
        </Button>

        {clipboardRow && (
          <div className="flex items-center gap-2 bg-green-900/50 px-3 py-1.5 rounded-lg border border-green-500">
            <ClipboardPaste className="w-4 h-4 text-green-300" />
            <span className="text-green-200 text-sm font-medium">
              В буфере: {clipboardRow.route_name || clipboardRow.date || 'рейс'}
            </span>
            <button 
              onClick={() => setClipboardRow(null)}
              className="text-green-400 hover:text-white ml-2"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="flex-1" />

        <div className="text-gray-400 text-sm flex items-center gap-4">
          <span className="bg-slate-700 px-2 py-1 rounded">Ctrl+C</span>
          <span>копировать</span>
          <span className="bg-slate-700 px-2 py-1 rounded">Ctrl+V</span>
          <span>вставить</span>
          <span className="text-gray-600">|</span>
          <span>Окон: {windows.length}</span>
        </div>
      </div>

      {/* Windows container */}
      <div 
        ref={containerRef}
        className="flex-1 relative bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden"
        style={{ minHeight: 600 }}
      >
        {windows.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold text-gray-600 mb-2">Excel-режим управления рейсами</h2>
              <p className="text-gray-500 mb-4">Нажмите кнопку выше, чтобы открыть окно</p>
              <Button onClick={openNewWindow} size="lg" className="bg-green-600 hover:bg-green-700">
                <Plus className="w-5 h-5 mr-2" />
                Открыть первое окно
              </Button>
            </div>
          </div>
        ) : (
          windows.map(win => (
            <ExcelWindow
              key={win.id}
              id={win.id}
              drivers={drivers}
              buses={buses}
              onClose={closeWindow}
              initialPosition={win.position}
              clipboardRow={clipboardRow}
              onCopyRow={handleCopyRow}
              zIndex={getZIndex(win.id)}
              onFocus={handleFocus}
              isActive={focusedWindow === win.id}
              registerWindow={registerWindow}
              unregisterWindow={unregisterWindow}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>

      {/* Help footer */}
      <div className="bg-slate-800 px-4 py-2 text-xs text-gray-400 flex items-center gap-6">
        <span>💡 Двойной клик — редактировать ячейку</span>
        <span>📋 Копирование строк через кнопку ⎘</span>
        <span>🟢 Новый</span>
        <span>🟡 Изменённый</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-1 bg-red-500"></span> Сегодня
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-1 bg-blue-500"></span> Будущие
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-1 bg-orange-500 rounded-full"></span> Остановки
        </span>
      </div>
    </div>
  );
}
